
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Edit, Trash2, XCircle } from "lucide-react";
import { useSubscriberManager } from "@/hooks/useSubscriberManager";
import { useCategories } from "@/hooks/useCategories";
import { ScrollArea } from "@/components/ui/scroll-area";
import EditSubscriberForm from "./EditSubscriberForm";
import { SubscriberInfo } from "./details/SubscriberInfo";
import { SubscriptionInfo } from "./details/SubscriptionInfo";
import { DeleteDialog } from "./details/DeleteDialog";

interface SubscriptionDetailsProps {
  subscriberId: string;
}

export default function SubscriptionDetails({ subscriberId }: SubscriptionDetailsProps) {
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteAllData, setDeleteAllData] = useState(true);
  
  const { subscribers, isLoading, cancelSubscription, deleteSubscriber } = useSubscriberManager();
  const { categories } = useCategories();
  
  const subscriber = subscribers?.find(s => s.id === subscriberId);
  
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full inline-block mr-2"></div>
        Loading subscription details...
      </div>
    );
  }
  
  if (!subscriber) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Subscriber not found
      </div>
    );
  }
  
  // Get the first active subscription (if any)
  const activeSubscription = subscriber.subscriptions && subscriber.subscriptions.length > 0 
    ? subscriber.subscriptions[0] 
    : null;
  
  const hasActiveSubscription = subscriber.status === 'active' && activeSubscription?.status === 'active';
  
  const handleCancel = () => {
    if (subscriber && activeSubscription) {
      cancelSubscription.mutate({
        userId: subscriber.id,
        subscriptionId: activeSubscription.id
      });
      setIsConfirmingCancel(false);
    }
  };
  
  const handleDelete = () => {
    if (subscriber) {
      deleteSubscriber.mutate({
        userId: subscriber.id,
        removeData: deleteAllData
      });
      setIsConfirmingDelete(false);
    }
  };

  const closeEditDialog = () => {
    setIsEditing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">
          {subscriber.phoneNumber}
        </h3>
        <div className="space-x-2">
          {hasActiveSubscription && (
            <>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="border-amber-300 text-amber-600 hover:bg-amber-50"
                onClick={() => setIsConfirmingCancel(true)}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            size="sm"
            className="border-red-300 text-red-600 hover:bg-red-50"
            onClick={() => setIsConfirmingDelete(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      <SubscriberInfo subscriber={subscriber} />
      
      <SubscriptionInfo 
        subscription={activeSubscription}
        hasActiveSubscription={hasActiveSubscription}
      />

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={isConfirmingCancel} onOpenChange={setIsConfirmingCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this subscription? The subscriber will no longer receive quotes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, keep subscription</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-amber-600 hover:bg-amber-700">
              Yes, cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Subscriber Dialog */}
      <DeleteDialog
        open={isConfirmingDelete}
        onOpenChange={setIsConfirmingDelete}
        onConfirm={handleDelete}
        deleteAllData={deleteAllData}
        onDeleteDataChange={setDeleteAllData}
      />

      {/* Edit Subscription Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
          </DialogHeader>
          <Separator />
          <ScrollArea className="max-h-[70vh] pr-4">
            {subscriber && activeSubscription && (
              <EditSubscriberForm 
                subscriberId={subscriber.id}
                initialCategories={activeSubscription.categories.map(c => c.id)}
                initialTimeSlots={activeSubscription.deliveryTimes}
                initialTimezone={subscriber.timezone}
                allCategories={categories || []}
                onClose={closeEditDialog}
              />
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
